import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "types/store";
import { Button } from "aible";

import Select from "react-select";

import * as viewReducer from "containers/View/reducer";
import * as chatAppReducer from "containers/ChatApp/reducer";
import * as blueprintReducer from "containers/Blueprint/reducer";

import { LineInput } from "components/Inputs/LineInput";

import SuccessRocketImg from "images/success-rocket.svg";

import styles from "./style.module.css";
import Tooltip from "components/Inputs/Tooltip";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";

const TestChatApp = (props = {}) => {
  const { id, showModal, updateShowModal } = props;

  const dispatch = useDispatch();

  const chatApp = useSelector((state) => blueprintReducer.getBlueprint(state, id));
  const chatAppCurrentTest = useSelector((state) => chatAppReducer.getCurrentAZTest(state, id));

  const isCreate = isNaN(id) || !chatApp;
  const currTest =
    isCreate || !chatAppCurrentTest
      ? {
          testApps: [],
          appDistribution: {}
        }
      : chatAppCurrentTest;
  const currTestApps = currTest?.testApps ?? [];
  const currAppDistribution = currTest?.appDistribution ?? {};

  const chatApps = useSelector((state) => viewReducer.getChatApps(state));
  const isLoading = useSelector((state) => chatAppReducer.getIsCreatingApp(state));

  const createAZTestApp = (name, apps, distribution) =>
    dispatch(chatAppReducer.createAZTestApp(name, apps, distribution));
  const updateAZTestApp = (id, name, apps, distribution) =>
    dispatch(chatAppReducer.updateAZTestApp(id, name, apps, distribution));

  const [name, updateName] = useState(chatApp?.name);
  const [apps, updateApps] = useState(currTestApps);
  const [appDistribution, updateAppDistribution] = useState(currAppDistribution);
  const [showWinnerModal, updateShowWinnerModal] = useState(false);
  const [selectedWinner, updateSelectedWinner] = useState(undefined);
  const [showWinnerConfirmModal, updateShowWinnerConfirmModal] = useState(false);

  const handleCloseModal = () => {
    updateShowModal(false);
  };

  useEffect(() => {
    updateName(chatApp?.name);
    updateApps(currTestApps);
    updateAppDistribution(currAppDistribution);
    updateShowWinnerModal(false);
    updateSelectedWinner(undefined);
    updateShowWinnerConfirmModal(false);
  }, [id]);

  const handleCreate = () => {
    if (isCreate) {
      return createAZTestApp(name, apps, appDistribution).then(() => {
        updateName(undefined);
        updateApps([]);
        updateAppDistribution({});
        handleCloseModal();
      });
    } else {
      return updateAZTestApp(id, name, apps, appDistribution).then(() => {
        handleCloseModal();
      });
    }
  };

  const handleWinner = (appId) => {
    const retDistribution = {};
    apps.forEach((a) => {
      retDistribution[a] = 0;
      if (a === appId) {
        retDistribution[a] = 100;
      }
    });

    updateAppDistribution(retDistribution);
    updateAZTestApp(id, name, apps, retDistribution).then(() => {
      updateShowWinnerModal(false);
      handleCloseModal();
      updateShowWinnerConfirmModal(true);
    });
  };

  const handleCloseWinnerConfirmModal = () => {
    updateShowWinnerConfirmModal(false);
    updateSelectedWinner(undefined);
    handleCloseModal();
  };

  const selectOptions = chatApps
    ?.filter((a) => {
      return a?.wizard?.isChatApp && !a?.wizard?.isAZTestApp && !a?.wizard?.isSampleApp;
    })
    ?.map((a) => {
      // console.log(a);
      return {
        value: a.id,
        label: a.name
      };
    });

  const selectValue = apps.map((appId) => {
    const sOption = selectOptions.find((o) => o.value === appId);

    if (!sOption) {
      return {
        value: appId,
        label: `Chat App ${appId}`
      };
    }

    return sOption;
  });

  const remainingDistribution = selectValue.reduce((acc, v) => {
    const currVal = appDistribution[v.value] ?? 0;
    return acc - currVal;
  }, 100);

  if (showWinnerConfirmModal) {
    return (
      <Dialog
        open={showWinnerConfirmModal}
        onOpenChange={() => {
          handleCloseWinnerConfirmModal();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Your App Winner Has Been Selected</DialogTitle>
          </DialogHeader>
          <div className={styles.confirmModalBody}>
            <div className={styles.confirmImgWrapper}>
              <img className={styles.confirmImg} src={SuccessRocketImg} />
            </div>
            <div className={styles.winnerText}>{"Congratulations!"}</div>
            <div className={styles.winnerText}>{`${selectedWinner?.label} has been published to all users.`}</div>
          </div>
          <DialogFooter>
            <Button
              type="secondary"
              onClick={() => {
                handleCloseWinnerConfirmModal();
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (showWinnerModal) {
    return (
      <Dialog
        open={showWinnerModal}
        onOpenChange={() => {
          updateShowWinnerModal(false);
          handleCloseModal();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{`Confirm ${selectedWinner?.label} as Winner`}</DialogTitle>
          </DialogHeader>

          <div className={styles.testConfirmationText}>{`Set ${selectedWinner?.label} as the winner?`}</div>
          <div className={styles.testConfirmationText}>
            {`This will remove all other apps from the A to Z test and redirect users to ${selectedWinner?.label}`}
          </div>
          <div className={styles.testConfirmationText}>{"Would you like to proceed?"}</div>

          <DialogFooter>
            <Button
              type="secondary"
              onClick={() => {
                updateShowWinnerModal(false);
                updateSelectedWinner(undefined);
              }}
              style={{ marginRight: "auto" }}
            >
              {"Cancel"}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                handleWinner(selectedWinner?.value);
              }}
            >
              {"Yes, Publish Winner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={showModal} onOpenChange={() => handleCloseModal()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${isCreate ? "Create" : "Update"} A to Z Test`}</DialogTitle>
        </DialogHeader>
        <div className={styles.modalBody}>
          <div className={styles.modalItem}>
            <div className={styles.modalLabel}>{"A to Z Test Name"}</div>
            <div className={styles.modalContent}>
              <LineInput
                isBox
                className={styles.modalInput}
                inputStyle={{ width: "100%" }}
                placeholder={"Name your test"}
                value={name}
                onChange={(val) => updateName(val)}
              />
            </div>
          </div>
          <div className={styles.modalItem}>
            <div className={styles.modalItemLabels}>
              <div className={styles.testModalLabel}>{"Agents in test"}</div>
              <div className={styles.testModalLabel}>{"Distribution"}</div>
            </div>
            <div className={styles.modalContent}>
              {selectValue.map((app, index) => {
                return (
                  <div className={styles.appItem} key={"appItem__" + app?.value + "__" + index}>
                    <div className={styles.appName}>{app?.label}</div>
                    <div className={styles.distributionInput}>
                      <LineInput
                        style={{ width: "40px" }}
                        value={appDistribution[app.value] ?? 0}
                        onChange={(val) => {
                          const retDistribution = {
                            ...appDistribution,
                            [app.value]: Number(val) || 0
                          };
                          updateAppDistribution(retDistribution);
                        }}
                      />
                      <span style={{ marginLeft: "4px" }}>{"%"}</span>
                    </div>
                    <div className={styles.selectedAppActions}>
                      <Button
                        isSmall
                        style={{ marginRight: "10px" }}
                        onClick={() => {
                          const retApps = apps.filter((i) => i !== app?.value);

                          const retDistribution = {};
                          let remaining = 100;
                          retApps.forEach((rId, i) => {
                            let currVal = Math.round(100 / (retApps.length || 1));
                            if (i === retApps.length - 1) {
                              currVal = remaining;
                            }
                            remaining -= currVal;
                            retDistribution[rId] = currVal || 0;
                          });
                          console.log("retApps", retApps);
                          updateApps(retApps);
                          updateAppDistribution(retDistribution);
                        }}
                      >
                        {"Remove"}
                      </Button>
                      <Button
                        isSmall
                        onClick={() => {
                          console.log("Opening winner modal and selecting app: ", app?.value);
                          updateShowWinnerModal(true);
                          updateSelectedWinner({ value: app?.value, label: app?.label });
                        }}
                        disabled={selectValue?.length < 2 || appDistribution[app?.value] === 100}
                      >
                        {"Winner"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.modalContent}>
              {selectValue?.length ? (
                remainingDistribution < 0 ? (
                  <div className={styles.modalDistributionWarning}>
                    {`${Math.abs(remainingDistribution)}% over allocated`}
                  </div>
                ) : remainingDistribution > 0 ? (
                  <div className={styles.modalDistributionWarning} style={{ color: "#7c7c7c" }}>
                    {`${remainingDistribution}% unallocated`}
                  </div>
                ) : null
              ) : null}
            </div>
            <div className={styles.modalContent}>
              <Select
                isBox
                placeholder={"Select an app to add"}
                value={null}
                onChange={(val) => {
                  const retApps = (apps ?? []).concat([val?.value]);

                  const retDistribution = {};
                  let remaining = 100;
                  retApps.forEach((rId, i) => {
                    let currVal = Math.round(100 / (retApps.length || 1));
                    if (i === retApps.length - 1) {
                      currVal = remaining;
                    }
                    remaining -= currVal;
                    retDistribution[rId] = currVal || 0;
                  });
                  updateApps(retApps);
                  updateAppDistribution(retDistribution);
                }}
                options={selectOptions.filter((o) => !apps?.includes(o.value))}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="secondary" onClick={() => handleCloseModal()} style={{ marginRight: "auto" }}>
            {"Cancel"}
          </Button>
          <Tooltip
            trigger={remainingDistribution || !selectValue?.length ? ["hover"] : []}
            overlay={!selectValue?.length ? "Select Apps to test" : "Apps must be fully distributed"}
          >
            <Button
              type="secondary"
              onClick={() => handleCreate()}
              isLoading={isLoading}
              disabled={remainingDistribution}
            >
              {`${isCreate ? "Create" : "Update"} A to Z Test App`}
            </Button>
          </Tooltip>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TestChatApp;
